<template>
    <div class="headline_card">
        <div class="card_inner">
            <div class="card_header is-flex is-justify-content-space-between is-align-items-center">
                <span class="period_label">{{ periodLabel }}</span>
                <div class="pills">
                    <span v-if="hasMom" class="pill" :class="pillClass(headline.mom)">
                        {{ formatPct(headline.mom) }} MoM
                    </span>
                    <span v-if="hasYoy" class="pill" :class="pillClass(headline.yoy)">
                        {{ formatPct(headline.yoy) }} YoY
                    </span>
                </div>
            </div>
            <p class="main_amount">{{ formatMoney(headline.amount) }}</p>
        </div>

        <div v-if="breakdown.length > 0" class="breakdown">
            <div class="breakdown_header is-flex is-align-items-center">
                <PieChartIcon class="breakdown_icon" />
                <span class="breakdown_title">Ventas</span>
            </div>
            <ul class="breakdown_list">
                <li v-for="item in breakdown" :key="item.account"
                    class="breakdown_item is-flex is-justify-content-space-between">
                    <span class="breakdown_account">{{ item.account }}</span>
                    <span class="breakdown_amount">{{ formatMoney(item.amount) }}</span>
                </li>
            </ul>
        </div>
    </div>
</template>

<script>
import { parseToMoney } from "@/utils/currency";
import { formatVariation } from "@/utils/percentage";
import PieChartIcon from "@/components/svg/PieChartIcon.vue";

export default {
    name: "HeadlineMonthCard",
    components: { PieChartIcon },
    props: {
        periodLabel: { type: String, default: "" },
        headline: {
            type: Object,
            required: true,
            validator: (v) => v && typeof v.amount === "number",
        },
    },
    computed: {
        breakdown() {
            return this.headline.breakdown ?? [];
        },
        hasMom() {
            return this.headline.mom != null;
        },
        hasYoy() {
            return this.headline.yoy != null;
        },
    },
    methods: {
        formatMoney(v) {
            return parseToMoney(v);
        },
        formatPct(pct) {
            // Pills sin arrow ni + explícito: el color del pill conduce la
            // dirección (verde/rojo/neutro). Los negativos sí conservan el "-"
            // porque el color solo no es suficiente para accesibilidad.
            return formatVariation(pct, { dash: "" });
        },
        pillClass(ratio) {
            if (ratio == null) return "pill--neutral";
            if (ratio > 0) return "pill--positive";
            if (ratio < 0) return "pill--negative";
            return "pill--neutral";
        },
    },
};
</script>

<style scoped lang="scss">
.headline_card {
    background: var(--report-bg);
    border-radius: 16px;
    border: 1px solid var(--report-border);
    box-shadow: 0 1px 2px rgba(10, 13, 20, 0.03);
    padding: 12px;
    height: 100%;
    display: flex;
    flex-direction: column;
    // El card es el clamp: nada se escapa de su caja. El monto+pills siempre
    // visibles; el breakdown scrollea dentro si crece.
    overflow: hidden;
}

// Inner surface: agrupa monto + pills + período sobre --report-surface (gris),
// separándolos visualmente del breakdown que vive en el outer (--report-bg).
// Es la única razón por la que se mantiene el inner — earned its existence.
.card_inner {
    padding: 20px;
    background: var(--report-surface);
    border-radius: 12px;
    border: 1px solid var(--report-border);
    // flex-shrink:0 mantiene el monto siempre completamente legible — la card
    // puede achicarse pero el headline no se comprime.
    flex-shrink: 0;
}

.card_header {
    margin-bottom: 12px;
}

.period_label {
    color: var(--report-text-muted);
    font-size: 14px;
    font-weight: 500;
}

.pills {
    display: flex;
    gap: 6px;
}

.pill {
    padding: 2px 10px;
    border-radius: 12px;
    font-size: 12px;
    font-weight: 600;
    border: 1px solid;
}

// Color semántico por dirección — mismo tratamiento para MoM y YoY: la
// distinción es el VALOR, no la métrica. Si en el futuro hubiera que
// diferenciar las métricas, se hace por orden/posición, no por color.
.pill--positive {
    background: var(--report-positive-bg);
    color: var(--report-positive);
    border-color: var(--report-positive-border);
}

.pill--negative {
    background: var(--report-negative-bg);
    color: var(--report-negative);
    border-color: var(--report-negative-border);
}

.pill--neutral {
    background: var(--report-surface);
    color: var(--report-text-muted);
    border-color: var(--report-border);
}

.main_amount {
    font-size: 36px;
    font-weight: 600;
    color: var(--report-text);
    letter-spacing: -0.5px;
    margin: 0;
    line-height: 1.1;
}

// Breakdown vive fuera del card_inner — sobre --report-bg outer. Top padding
// genera el aire entre la caja gris y el header de la sección. flex:1 +
// min-height:0 lo deja absorber el sobrante y permitir scroll interno.
.breakdown {
    padding: 16px 4px 0;
    flex: 1;
    min-height: 0;
    display: flex;
    flex-direction: column;
}

.breakdown_header {
    margin-bottom: 8px;
    color: var(--report-text);
    flex-shrink: 0;

    .breakdown_icon {
        width: 16px;
        height: 16px;
        margin-right: 8px;
        flex-shrink: 0;
        color: var(--report-text);
    }

    .breakdown_title {
        font-size: 14px;
        font-weight: 600;
    }
}

.breakdown_list {
    list-style: none;
    margin: 0;
    padding: 0;
    flex: 1;
    min-height: 0;
    overflow-y: auto;
}

.breakdown_item {
    padding: 10px 0;
    border-bottom: 1px solid var(--report-divider-soft);
    font-size: 14px;

    &:last-child {
        border-bottom: none;
    }

    .breakdown_account {
        color: var(--report-text);
        font-weight: 600;
    }

    .breakdown_amount {
        color: var(--report-text);
        font-weight: 600;
    }
}
</style>
